import { onMounted, onUnmounted } from 'vue'
import { onIdTokenChanged } from 'firebase/auth'
import { auth } from '@/firebase'
import { platformCoordApi } from '@/components/cloudFunctions/platformCoord'
import { getErrorCode } from '@/utils/errorUtils'

const CONSULTATION_UX_STACK_PREWARM_INTERVAL_MS = 60_000

// Slack so a slightly early interval tick is not mistaken for a duplicate.
const COALESCE_WINDOW_MS = CONSULTATION_UX_STACK_PREWARM_INTERVAL_MS - 1_000

// Shared across hub, book card, and booking host so navigating between them does not re-ping.
let lastPingAt = Number.NEGATIVE_INFINITY

export function resetConsultationUxStackPrewarmCoalesce(): void {
  lastPingAt = Number.NEGATIVE_INFINITY
}

function isConsultationUxStackPrewarmAllowed(): boolean {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    return false
  }
  return auth.currentUser != null
}

export function pingConsultationUxStack(options?: { onUnauthenticated?: () => void }): void {
  if (!isConsultationUxStackPrewarmAllowed()) {
    return
  }
  const now = Date.now()
  if (now - lastPingAt < COALESCE_WINDOW_MS) {
    return
  }
  lastPingAt = now
  void platformCoordApi.prewarmConsultationStack({}).catch((error: unknown) => {
    if (getErrorCode(error) === 'functions/unauthenticated') {
      // Allow an immediate prewarm after re-auth within the coalesce window.
      lastPingAt = Number.NEGATIVE_INFINITY
      options?.onUnauthenticated?.()
    }
  })
}

export function useConsultationUxStackPrewarm(): void {
  let intervalId: number | undefined
  let unsubscribeIdToken: (() => void) | undefined

  const ping = () => {
    pingConsultationUxStack({ onUnauthenticated: stop })
  }

  function stop(): void {
    if (intervalId !== undefined) {
      window.clearInterval(intervalId)
      intervalId = undefined
    }
    document.removeEventListener('visibilitychange', ping)
  }

  function start(): void {
    stop()
    ping()
    intervalId = window.setInterval(ping, CONSULTATION_UX_STACK_PREWARM_INTERVAL_MS)
    document.addEventListener('visibilitychange', ping)
  }

  onMounted(() => {
    // onIdTokenChanged fires immediately with the current user, then on sign-in/refresh/sign-out.
    // Do not reset coalesce on remount — hub/book/signup share lastPingAt across navigation.
    unsubscribeIdToken = onIdTokenChanged(auth, (user) => {
      if (user) {
        if (intervalId === undefined) {
          start()
        }
      } else {
        stop()
        resetConsultationUxStackPrewarmCoalesce()
      }
    })
  })

  onUnmounted(() => {
    stop()
    unsubscribeIdToken?.()
    unsubscribeIdToken = undefined
  })
}
